import type { WhatIfSliderKey } from "../hooks/useWhatIfScenario";

export interface SliderConfig {
  key: WhatIfSliderKey;
  label: string;
  min: number;
  max: number;
  step: number;
  /** Format value for display (e.g., "6.0%" or "65") */
  format: (value: number) => string;
  /** Parse display value to slider numeric value */
  toSlider: (value: number) => number;
  /** Parse slider value to data value */
  fromSlider: (value: number) => number;
}

/** Default static slider configs (backward compatibility) */
export const SLIDER_CONFIGS: SliderConfig[] = buildSliderConfigs(55);

/**
 * Get slider configs with dynamic min/max based on user's currentAge.
 * F-03: Prevents retirementAge < currentAge.
 * F-04: Keeps lifeExpectancy min > retirementAge min to prevent zero retirement years.
 */
export function getSliderConfigs(currentAge?: number): SliderConfig[] {
  const retirementMin = currentAge != null ? Math.max(55, currentAge + 1) : 55;
  return buildSliderConfigs(retirementMin);
}

function buildSliderConfigs(retirementMin: number): SliderConfig[] {
  // Ensure lifeExpectancy min is always at least retirementAge min + 1
  const lifeExpectancyMin = Math.max(75, retirementMin + 1);

  return [
    {
      key: "retirementAge",
      label: "Retirement Age",
      min: retirementMin,
      max: 75,
      step: 1,
      format: (v) => String(Math.round(v)),
      toSlider: (v) => v,
      fromSlider: (v) => Math.round(v),
    },
    {
      key: "lifeExpectancy",
      label: "Life Expectancy",
      min: lifeExpectancyMin,
      max: 100,
      step: 1,
      format: (v) => String(Math.round(v)),
      toSlider: (v) => v,
      fromSlider: (v) => Math.round(v),
    },
    {
      key: "expectedReturnRate",
      label: "Expected Return Rate",
      min: 1,
      max: 12,
      step: 0.5,
      format: (v) => `${(v * 100).toFixed(1)}%`,
      toSlider: (v) => v * 100,
      fromSlider: (v) => v / 100,
    },
    {
      key: "inflationRate",
      label: "Inflation Rate",
      min: 0,
      max: 6,
      step: 0.1,
      format: (v) => `${(v * 100).toFixed(1)}%`,
      toSlider: (v) => v * 100,
      fromSlider: (v) => v / 100,
    },
    {
      key: "expensesAtRetirement",
      label: "Expenses at Retirement",
      min: -50,
      max: 30,
      step: 5,
      format: (v) => `${v >= 0 ? "+" : ""}${(v * 100).toFixed(0)}%`,
      toSlider: (v) => v * 100,
      fromSlider: (v) => v / 100,
    },
  ];
}
